import { createSelector } from 'reselect';
import some from 'lodash/some';
import get from 'lodash/get';

import { makeSelectorFactory } from '../../utils/redux/selectAdapter';

export const selectRootState = state => get(state, 'healthboard');
const getSelectors = makeSelectorFactory(selectRootState);

/**
 *  PATIENT UPCOMING APPOINTMENTS
 * */
export const {
  selectStatePending: selectUpcomingAppointmentsPending,
  selectStateList: selectUpcomingAppointmentList,
  makeSelect: makeUpcomingListSelect,
} = getSelectors('upcomingAppointments');

export const selectUpcomingPaginationPending = makeUpcomingListSelect(
  'paginationSending',
);
export const selectUpcomingPagination = makeUpcomingListSelect('pagination');
export const selectUpcomingCount = makeUpcomingListSelect('count', 0);
export const selectMetaAvailableUpcomingList = makeUpcomingListSelect(
  'meta.historyAvailable',
  false,
);

/**
 *  PATIENT HISTORY APPOINTMENTS
 * */
export const {
  selectStatePending: selectHistoryAppointmentsPending,
  selectStateList: selectHistoryAppointmentList,
  makeSelect: makeHistoryListSelect,
} = getSelectors('historyAppointments');

export const selectHistoryPaginationPending = makeHistoryListSelect(
  'paginationSending',
);
export const selectHistoryPagination = makeHistoryListSelect('pagination');
export const selectHistoryCount = makeHistoryListSelect('count', 0);

// =========================================================
/**
 *  PROVIDER UPCOMING APPOINTMENTS
 * */
export const {
  selectStatePending: selectProviderUpcomingAppointmentsPending,
  selectStateList: selectProviderUpcomingAppointmentList,
  makeSelect: makeProviderUpcomingListSelect,
} = getSelectors('providerUpcomingAppointments');

/**
 *  PROVIDER FILTERS UPCOMING APPOINTMENTS
 * */
export const selectProviderUpcomingFilterToday = createSelector(
  selectRootState,
  state => get(state, 'providerFilterUpcomingAppointments.isFilterToday'),
);

export const selectProviderUpcomingFilterByTypeVisit = createSelector(
  selectRootState,
  state => get(state, 'providerFilterUpcomingAppointments.isFilterByTypeVisit'),
);

export const selectProviderUpcomingPagination = makeProviderUpcomingListSelect(
  'pagination',
);
export const selectProviderUpcomingPaginationPending = makeProviderUpcomingListSelect(
  'paginationSending',
);
export const selectProviderUpcomingCount = makeProviderUpcomingListSelect(
  'count',
  0,
);

/**
 *  PROVIDER HISTORY APPOINTMENTS
 * */
export const {
  selectStatePending: selectProviderHistoryAppointmentsPending,
  selectStateList: selectProviderHistoryAppointmentList,
  makeSelect: makeProviderHistoryListSelect,
} = getSelectors('providerHistoryAppointments');

export const selectProviderHistoryPagination = makeProviderHistoryListSelect(
  'pagination',
);
export const selectProviderHistoryPaginationPending = makeProviderHistoryListSelect(
  'paginationSending',
);
export const selectProviderHistoryCount = makeProviderHistoryListSelect(
  'count',
  0,
);
// =========================================================

/**
 *  SCHEDULED TASKS LIST
 * */
export const {
  selectStatePending: selectScheduledTasksPending,
  selectStateList: selectScheduledTasks,
  makeSelect: makeScheduledTasksSelect,
} = getSelectors('scheduledTasks');

export const selectScheduledTasksPagination = makeScheduledTasksSelect(
  'pagination',
);
export const selectScheduledTasksCount = makeScheduledTasksSelect('count', 0);
export const selectScheduledTasksPaginationSending = makeScheduledTasksSelect(
  'paginationSending',
);
export const selectMetaAvailableScheduledTasks = makeScheduledTasksSelect(
  'meta.historyAvailable',
  false,
);

// =========================================================

/**
 *  COMPLETED SCHEDULED TASKS LIST
 * */
export const {
  selectStatePending: selectScheduledCompletedTasksPending,
  selectStateList: selectScheduledCompletedTasks,
  makeSelect: makeCompletedTasksSelect,
} = getSelectors('completedTasks');

export const selectScheduledCompletedTasksPagination = makeCompletedTasksSelect(
  'pagination',
);
export const selectScheduledCompletedTasksCount = makeCompletedTasksSelect(
  'count',
  0,
);
export const selectScheduledCompletedTasksPaginationSending = makeCompletedTasksSelect(
  'paginationSending',
);

// =========================================================

/**
 *  CHECK-IN APPOINTMENT
 * */
export const {
  selectStatePending: selectCheckInAppointmentPending,
} = getSelectors('checkinAppointment');

/**
 *  CANCEL APPOINTMENT
 * */

export const {
  selectStatePending: selectCancelAppointmentPending,
} = getSelectors('cancelAppointment');

/**
 *  APPOINTMENT DETAILS
 * */
const selectActiveAppointmentState = createSelector(
  selectRootState,
  state => get(state, 'appointmentDetails', {}),
);
export const selectActiveAppointmentId = createSelector(
  selectActiveAppointmentState,
  state => get(state, 'activeId'),
);
export const selectActiveAppointmentCoupon = createSelector(
  selectActiveAppointmentState,
  state => get(state, 'coupon'),
);

export const selectActiveAppointment = createSelector(
  selectHistoryAppointmentList,
  selectUpcomingAppointmentList,
  selectActiveAppointmentId,
  (history, upcoming, appointmentId) => {
    if (!appointmentId) return undefined;

    return [...history, ...upcoming].find(({ id }) => id === appointmentId);
  },
);

export const selectProviderActiveAppointment = createSelector(
  selectProviderHistoryAppointmentList,
  selectProviderUpcomingAppointmentList,
  selectActiveAppointmentId,
  (history, upcoming, appointmentId) => {
    if (!appointmentId) return undefined;

    return [...history, ...upcoming].find(({ id }) => id === appointmentId);
  },
);

/**
 *  COUPON VERIFICATION
 * */
export const {
  selectStatePending: selectCouponVerificationPending,
  selectStateData: selectCouponVerificationData,
  makeSelect: makeCouponVerificationSelect,
} = getSelectors('couponVerification');
export const selectCouponAvailability = makeCouponVerificationSelect(
  'data.couponsAvailable',
);

/**
 *  COUPON VERIFICATION
 * */
export const {
  selectStatePending: selectCouponCalculationPending,
  selectStateData: selectCouponCalculationData,
  selectStateError: selectCouponCalculationError,
} = getSelectors('couponCalculation');

/**
 *  STATUS CHANGES
 * */
export const { selectStatePending: selectStatusChangesPending } = getSelectors(
  'statusChanges',
);

/**
 *  PAYMENT VERIFICATION
 * */
export const {
  selectStatePending: selectPaymentVerificationPending,
} = getSelectors('paymentVerifications');

/**
 *  PAYMENT ORDER
 * */
export const {
  selectStatePending: selectPaymentOrderPending,
  selectStateData: selectPaymentOrderStateData,
} = getSelectors('paymentOrder');

export const selectPaymentOrderAmount = createSelector(
  selectPaymentOrderStateData,
  state => get(state, 'amount', 0),
);

/**
 *  PAYA REDIRECTION
 * */
export const {
  selectStatePending: selectPayaRedirectionPending,
} = getSelectors('payaRedirection');

/**
 *  EDIT PAYMENT
 * */
export const {
  selectStatePending: selectEditPaymentPending,
  selectStateError: selectEditPaymentError,
} = getSelectors('editPayment');

/**
 *  UPLOAD DOCUMENT
 * */
export const {
  selectStatePending: selectUploadDocumentPending,
  selectStateError: selectUploadDocumentError,
} = getSelectors('uploadDocument');

/**
 *  DELETE DOCUMENT
 * */
export const {
  selectStatePending: selectDeleteDocumentPending,
  selectStateError: selectDeleteDocumentError,
} = getSelectors('deleteDocument');

/**
 *  APPOINTMENT CONSENTS MODAL
 * */
export const selectAppointmentConsentsModalState = createSelector(
  selectRootState,
  state => get(state, 'appointmentConsentsModal'),
);

export const selectAppointmentConsentsModalOpen = createSelector(
  selectAppointmentConsentsModalState,
  state => get(state, 'opened', false),
);

export const selectAppointmentConsentsModalOpenUrgentVisit = createSelector(
  selectAppointmentConsentsModalState,
  state => get(state, 'urgentVisitFlow', false),
);

export const selectAppointmentConsentsModalOpenSelfScheduling = createSelector(
  selectAppointmentConsentsModalState,
  state => get(state, 'selfSchedulingFlow', false),
);

export const selectAppointmentConsentsModalOpenRescheduleFlow = createSelector(
  selectAppointmentConsentsModalState,
  state => get(state, 'rescheduleFlow', {}),
);

export const selectAppointmentCurrentConsentState = createSelector(
  selectAppointmentConsentsModalState,
  state => get(state, 'consent', {}),
);

export const selectAppointmentServiceName = createSelector(
  selectAppointmentConsentsModalState,
  state => get(state, 'serviceName', ''),
);

export const selectAppointmentConsentTime = createSelector(
  selectAppointmentConsentsModalState,
  state => get(state, 'startTime', ''),
);

export const selectAppointmentOrganizationName = createSelector(
  selectAppointmentConsentsModalState,
  state => get(state, 'organizationName', ''),
);

export const selectAppointmentAllConsentState = createSelector(
  selectAppointmentConsentsModalState,
  state => get(state, 'allConsents', []),
);

export const selectIsMainConsentViewState = createSelector(
  selectAppointmentConsentsModalState,
  state => get(state, 'isMainConsentView', false),
);

export const selectPatientUserInfoIdWithAppointmentState = createSelector(
  selectAppointmentConsentsModalState,
  state => get(state, 'patientUserInfoId', null),
);

export const selectPaymentGlobalLoader = createSelector(
  selectPayaRedirectionPending,
  selectPaymentOrderPending,
  selectCouponVerificationPending,
  selectPaymentVerificationPending,
  selectCheckInAppointmentPending,
  selectCancelAppointmentPending,
  selectEditPaymentPending,
  selectUploadDocumentPending,
  selectDeleteDocumentPending,
  selectStatusChangesPending,
  (...pendings) => some(pendings, pending => pending),
);

/**
 *  MANAGE PENDING REQUEST
 * */

export const selectManagePendingRequestSending = createSelector(
  selectRootState,
  state => get(state, 'managePendingRequest.sending', false),
);

/**
 *  ROOT
 * */
export const selectRootSubState = createSelector(
  selectRootState,
  state => get(state, 'root', {}),
);
export const selectHealthboardValidation = createSelector(
  selectRootSubState,
  state => get(state, 'validating'),
);

/**
 *  ACTIVE T0D0 item
 * */
export const selectActiveTodoState = createSelector(
  selectRootState,
  state => get(state, 'activeTodo', {}),
);
export const selectActiveTodoItem = createSelector(
  selectActiveTodoState,
  state => get(state, 'activeItem'),
);

export const selectActiveTodoItemTaskId = createSelector(
  selectActiveTodoState,
  state => get(state, 'activeItem.todoTask.id', null),
);

export const selectActiveTodoItemTaskGeoLocations = createSelector(
  selectRootState,
  state => get(state, 'geoLocations.list', []),
);

// VIRTUAL VISIT UI
export const selectVirtualVisitState = createSelector(
  selectRootState,
  state => get(state, 'virtualVisit', {}),
);

export const selectVirtualVisitOpened = createSelector(
  selectVirtualVisitState,
  state => get(state, 'opened', false),
);
export const selectVirtualVisitMobSidebarOpened = createSelector(
  selectVirtualVisitState,
  state => get(state, 'isOpenedMobSidebar', false),
);
export const selectVirtualVisitCollapsed = createSelector(
  selectVirtualVisitState,
  state => get(state, 'collapsed', false),
);

export const selectVirtualVisitFullScreenView = createSelector(
  selectVirtualVisitState,
  state => get(state, 'fullScreenView', false),
);

export const selectVirtualVisitDragging = createSelector(
  selectVirtualVisitState,
  state => get(state, 'dragging', false),
);

export const selectVirtualVisitConfig = createSelector(
  selectVirtualVisitState,
  state => get(state, 'config'),
);
export const selectAppointmentIdFromConfig = createSelector(
  selectVirtualVisitConfig,
  config => get(config, 'appointmentId'),
);
export const selectInvitationTypeFromConfig = createSelector(
  selectVirtualVisitConfig,
  config => get(config, 'type'),
);

export const selectShouldSignConsent = createSelector(
  selectVirtualVisitConfig,
  state => get(state, 'shouldSignConsent'),
);

export const selectVirtualVisitActive = createSelector(
  selectVirtualVisitState,
  state => get(state, 'active', false),
);

/**
 *  COMPLETE TASK
 * */
export const {
  selectStatePending: selectCompleteTaskPending,
  selectStateSucceed: selectCompleteTaskSucceed,
  selectStateError: selectCompleteTaskError,
} = getSelectors('completeTask');

export const {
  selectStatePending: selectCompleteTaskCustomPending,
  selectStateSucceed: selectCompleteTaskCustomSucceed,
  selectStateError: selectCompleteTaskCustomError,
} = getSelectors('completeTaskCustom');

export const {
  selectStatePending: selectCompleteCarePlanTaskPending,
  selectStateSucceed: selectCompleteCarePlanTaskSucceed,
  selectStateError: selectCompleteCarePlanTaskError,
} = getSelectors('completeCarePlanTask');

/**
 *  TEST CALL CREDENTIALS
 * */
export const {
  selectStatePending: selectNetworkTestCredentialsPending,
  selectStateError: selectNetworkTestCredentialsError,
  selectStateData: selectNetworkTestCredentials,
} = getSelectors('networkTestCredentials');

/**
 *  ROOM INVITATION INFO (pre-join payment check)
 * */
export const {
  selectStatePending: selectRoomInvitationInfoPending,
  selectStateData: selectRoomInvitationInfoData,
  selectStateSucceed: selectRoomInvitationInfoSucceed,
  selectStateError: selectRoomInvitationInfoError,
  makeSelect: makeRoomInvitationInfoSelect,
} = getSelectors('roomInvitationInfo');

export const selectRoomInvitationInfoPaymentRequired = makeRoomInvitationInfoSelect(
  'meta.isRequiredPayment',
  false,
);

export const selectRoomInvitationInfoPatientName = createSelector(
  selectRoomInvitationInfoData,
  data => get(data, 'patientUserInfo.displayableName', ''),
);

export const selectRoomInvitationInfoAppointmentId = createSelector(
  selectRoomInvitationInfoData,
  data => get(data, 'id', null),
);

export const selectRoomInvitationInfoUserId = createSelector(
  selectRoomInvitationInfoData,
  data => get(data, 'userId', null),
);

export const selectRoomInvitationInfoCopay = createSelector(
  selectRoomInvitationInfoData,
  data => get(data, 'copay', null),
);

export const selectRoomInvitationInfoVisitTypeName = createSelector(
  selectRoomInvitationInfoData,
  data => get(data, 'visitType.name', ''),
);

/**
 *  ROOM INVITATION JOIN
 * */
export const {
  selectStateData: selectRoomInvitationJoinData,
  selectStateError: selectRoomInvitationJoinError,
  selectStateSucceed: selectRoomInvitationJoinSucceed,
  selectStatePending: selectRoomInvitationJoinPending,
  makeSelect: makeRoomInvitationSelector,
} = getSelectors('roomInvitationJoin');

export const selectRoomInvitationJoinRoomName = createSelector(
  selectRoomInvitationJoinData,
  state => get(state, 'patientUserInfo.displayableName', ''),
);

export const selectRoomInvitationJoinStatus = createSelector(
  selectRoomInvitationJoinData,
  state => get(state, 'status.coreAppName'),
);

export const selectRoomInvitationPatientProfile = makeRoomInvitationSelector(
  'meta.patientProfile',
  null,
);

export const selectRoomInvitationInvitee = makeRoomInvitationSelector(
  'meta.roomInvitation.invitee',
  null,
);

export const selectRoomInvitationRole = makeRoomInvitationSelector(
  'meta.roomInvitation.role',
  null,
);

/**
 *   CONNECTION ROOM JOIN
 * */
export const {
  selectStateData: selectConnectionRoomJoinData,
  selectStateError: selectConnectionRoomJoinError,
  selectStatePending: selectConnectionRoomJoinPending,
} = getSelectors('connectionRoomJoin');

/**
 *   JOIN ROOM BY CODE
 * */
export const {
  selectStateData: selectVirtualRoomByCodeData,
  selectStateError: selectVirtualRoomByCodeError,
  selectStatePending: selectVirtualRoomByCodePending,
} = getSelectors('joinByVisitCode');

/**
 *  INVITE GUEST TO VIRTUAL ROOM
 * */
export const { selectState: selectStateInviteUserToVirtualRoom } = getSelectors(
  'inviteUserToVirtualRoom',
);

export const selectCallInvitationsExisting = createSelector(
  selectStateInviteUserToVirtualRoom,
  data => get(data, 'existing'),
);

export const selectCurrentAppointmentId = createSelector(
  selectStateInviteUserToVirtualRoom,
  data => get(data, 'appointmentId'),
);

export const selectCallInvitationsSendState = createSelector(
  selectStateInviteUserToVirtualRoom,
  data => get(data, 'send'),
);
export const selectCallInvitationsSendLoading = createSelector(
  selectCallInvitationsSendState,
  data => get(data, 'sending'),
);
export const selectCallInvitationsSendError = createSelector(
  selectCallInvitationsSendState,
  data => get(data, 'error'),
);

/**
 *  DEVICE PERMISSIONS
 * */

const selectDevicePermissionState = createSelector(
  selectRootState,
  state => get(state, 'devicePermissions', {}),
);
export const selectDevicePerissionModalOpened = createSelector(
  selectDevicePermissionState,
  state => get(state, 'opened'),
);
export const selectDevicePerissionModalProps = createSelector(
  selectDevicePermissionState,
  state => get(state, 'props', {}),
);

/**
 *  TEST CALL
 * */

const selectTestCallState = createSelector(
  selectRootState,
  state => get(state, 'testCall', {}),
);
export const selectTestCallModalOpened = createSelector(
  selectTestCallState,
  state => get(state, 'opened'),
);
export const selectTestCallModalProps = createSelector(
  selectTestCallState,
  state => get(state, 'props', {}),
);

/**
 *  APPOINTMENT SURVEY ASSIGNMENT
 * */
export const selectCurrentAppointmentSurveys = createSelector(
  selectRootState,
  state => get(state, 'appointmentSurveyAssignment.surveys', []),
);

export const selectCurrentAppointmentAssignment = createSelector(
  selectRootState,
  state => get(state, 'appointmentSurveyAssignment.assignmentId', null),
);

/**
 *  SIGN APPOINTMENT CONSENT
 * */

export const selectSignAppointmentConsentLoading = createSelector(
  selectRootState,
  state => get(state, 'signAppointmentConsent.sending', false),
);

export const selectSignAppointmentConsentSucceed = createSelector(
  selectRootState,
  state => get(state, 'signAppointmentConsent.succeed', false),
);



// WEBPACK FOOTER //
// ./src/redux/healthboard/selectors.js